/***
 *
 *   DASHBOARD
 *   Template dashboard example demonstrating various components inside a view.
 *
 **********/

import React, { useEffect, useState, useContext } from 'react';
import Styles from './dashboard.module.scss'
import { useNavigate } from 'react-router-dom';

// Module imports.
import axios from 'axios';
import {
	List, ListItemButton, ListSubheader, ListItemText, Collapse,
	ListItem, Tabs, Tab, Divider, TextField, Autocomplete, Typography
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import StopIcon from '@mui/icons-material/Stop';
import { TabContext, TabPanel } from '@mui/lab';
import { CSVLink } from "react-csv";

// Components import.
import {
	Card, Stat, ProgressBar, Chart, Table, Message,
	Grid, Animate, useAPI, Loader, AuthContext, ViewContext,
	useCan
} from 'components/lib';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Icon } from 'components/icon/icon';
import { CourseOverView } from 'views/LMS/CourseOverView/CourseOverView';
import { PaymentPending } from 'views/account/paymentPending';
import { AssignResultsCard } from './AssignResultsCard';
import { CertScheduleForm } from './CertScheduleForm';

export function Dashboard(props) {

	const navigate = useNavigate();
	const context = useContext(AuthContext);
	const permission = useCan('dashboard.view')
	const isOrgAdmin = useCan('dashboard.admin.view'); 
	const isLMS = context.user.lms_enabled;
	//TODO Need to Remove this part on LMS plans phase out
	const lmsOnly = context.user.lms_only;
	const viewContext = useContext(ViewContext);
	const addOns = JSON.parse(localStorage.getItem("user")).addons;
	const isMetrics = addOns?.metrics?.enabled;


	const [dummyData, setDummyData] = useState({
		cert: 0, lab: 0, stream: 0, quiz: 0, capstone: 0,
		badges: {
			"labels": ["Gold", "Silver", "Bronze"],
			"datasets": [{
				"label": "Medals",
				"data": [0, 0, 0]
			}]
		},
		progress: [
			{ 'name': 'Certifications', 'value': 0 }, { 'name': 'Courses', 'value': 0 }, { 'name': 'Codeprep Skills', 'value': 0 }, { 'name': 'Capstone Projects', 'value': 0 },
		]
	});

	// useStates 
	const [scholarCertData, setScholarCertData] = useState([])
	const [showScholarLabs, setShowScholarLabs] = useState();
	const [userTrainingStats, setUserTrainingStats] = useState()
	const [show, setShow] = useState(true);
	const [userCourses, setUserCourses] = useState([]);
	const [userMetrics, setUserMetrics] = useState([])
	const [showLmsSchedule, setShowLmSchedule] = useState();
	const [lmsEnrollmentData, setlmsEnrollmentData] = useState({});
	const [quizEnrollmentData, setquizEnrollmentData] = useState({});
	const [progressDict, setProgressDict] = useState(dummyData);
	const [buId, setBuID] = useState('')
	const [formData, setFormData] = useState({
		userId: '',
		userName: ''
	});
	const [orgScholars, setOrgScholars] = useState([]);
	const [showSchedule, setShowSchedule] = useState(false);
	const [showAssignResults, setShowAssignResults] = useState(false);
	const [scheduleUserId, setScheduleUserId] = useState();
	const [assignResults, setAssignResults] = useState();
	const [value, setValue] = useState(lmsOnly ? "3" : "1");
	const [isSticky, setIsSticky] = useState(false);
	const [exportData, setExportData] = useState([]);
	const [accountCourse, setAccountCourse] = useState({
		courseId: '',
		courseTitle: '',
	})
	const [accountQuiz, setAccountQuiz] = useState({
		metricsId: '',
		metricsName: ''
	});

	// API call 
	const stats = useAPI('/api/dashboard');
	const trainingStat = useAPI((isOrgAdmin && permission) && '/api/dashboard/training/stats');
	const certData = useAPI((isOrgAdmin && permission) && '/api/dashboard/learning_paths/due_date/monthly');
	const lmsOverview = useAPI(((isOrgAdmin && permission) && isLMS) && '/api/lms/dashboard/progress');
	const lmsEnrollment = useAPI(((isOrgAdmin && permission) && isLMS) && '/api/lms/dashboard/course/overview');
	const quizOverview = useAPI((isOrgAdmin && permission) && isMetrics ? '/api/dashboard/metrics/progress' : null);
	const quizEnrollment = useAPI((isOrgAdmin && permission) && isMetrics ? '/api/dashboard/metrics/overview' : null);

	// useEffects
	useEffect(() => {
		if (lmsEnrollment?.data) {
			setlmsEnrollmentData(lmsEnrollment?.data)
		}
	}, [lmsEnrollment?.data]);


	useEffect(() => {
		if (quizEnrollment?.data) {
			setquizEnrollmentData(quizEnrollment?.data)
		}
	}, [quizEnrollment?.data]);

	useEffect(() => {
		if (stats && stats.data) {
			// Update dummyData with stats.data values
			const updatedDummyData = {
				...dummyData,
				cert: stats.data?.totalCert,
				lab: stats.data?.totalLab,
				stream: stats.data?.totalStream,
				quiz: stats.data?.totalQuiz,
				project: stats.data?.totalProject ?? 0,
			};
			setDummyData(updatedDummyData);

			setProgressDict((isOrgAdmin && permission) ? updatedDummyData : stats.data.userDashboard);

			if (stats.data.users) {
				setOrgScholars(stats.data?.users);
				const filteredUsers = stats.data?.users?.map(
					({ fullname, username, email, lms_score, metrics_score, lab_score, stream_score }) => ({
						fullname,
						username,
						email,
						Score: isLMS
							? lms_score
							: isMetrics
								? metrics_score
								: lab_score + stream_score,
					})
				);
				setExportData(filteredUsers)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stats]);

	useEffect(() => {
		const handleScroll = () => {
			const scrollPosition = window.scrollY;
			if (scrollPosition > 250) {
				setIsSticky(true);
			} else {
				setIsSticky(false);
			}
		};

		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	// Functions
	const handleChange = (e, values) => {
		const userId = values?.id || "";
		setShow(!!!userId)
		if (!!!userId) {
			setFormData({
				userId: '',
				userName: ''
			});
			setProgressDict(dummyData);
			return;
		}
		const userIndex = orgScholars.findIndex(user => user.id === userId)
		const newUserId = orgScholars[userIndex]?.id;
		if (formData.userId === newUserId) {
			return;
		}
		setFormData({
			userId: newUserId,
			userName: values
		});
		const options = {
			method: "GET",
			url: `/api/dashboard/${newUserId}`,
			headers: {
				"accept": "application/json",
			}
		};
		axios
			.request(options)
			.then(function (response) {
				setUserCourses(response.data.data.userCoursesData)
				setUserMetrics(response.data.data.userMetricsData)
				setScholarCertData(response.data.data.cert_data)
				setProgressDict(response.data.data.userDashboard);
				setUserTrainingStats(response.data.data.trainingMods)
			})
			.catch((err) => {
				if (err.response.status === 401) {
					navigate('/nopermission');
				}
			});
	};

	const getDashboardMessage = (isOwner) => {
		if (isOwner) {
			return props.t('dashboard.message.text.isOwner');
		} else {
			return props.t('dashboard.message.text.default');
		}
	};

	const ownerActions = {
		custom: [
			{ icon: 'calendar', action: scheduleCertificate },
			{ icon: 'file', action: assignCertificates },
			{ icon: 'monitor', action: assignLabs },
			{ icon: 'package', action: assignInternship },
		]
	}

	const handleNavigate = (path) => {
		// If it is an admin, do nothing.
		!(isOrgAdmin && permission) && navigate(path);
	}

	//represents which view => 1 - USERS, 2 - training view

	const handleViewChange = (event, newValue) => {
		setValue(newValue);
		if (stats.data.users) {
			setOrgScholars(stats.data?.users);
			const filteredUsers = stats.data?.users?.map(({ fullname, username, email, lms_score, metrics_score, lab_score, stream_score }) => ({
				fullname,
				username,
				email,
				Score: isLMS
					? lms_score
					: isMetrics
						? metrics_score
						: lab_score + stream_score,
			}));
			setExportData(filteredUsers)
		}
	};

	// Handle certifications actions
	function scheduleCertificate(data, callback) {
		
		if (data.Permissions.includes('cert.due.assign')) {  //Here it is captialized as data is set that way in the state
			setShowSchedule(true)
			setScheduleUserId(data.uuid)
		} else {
			viewContext.notification.show(props.t('dashboard.user.dueDateForm.notification'), 'warning', true);
		}
	}

	function assignCertificates(data, callback) {

		// Check if the data is multiple.
		const multi = Array.isArray(data);
		const user_ids = multi ? data.map(item => item?.row?.uuid) : [data.uuid];

		const options = {
			method: "GET",
			url: multi ? `/api/lpath/names` : `/api/lpath/names/?user_id=${user_ids[0]}`
		};
		axios
			.request(options)
			.then(function (response) {
				viewContext.modal.show({
					title: props.t('dashboard.user.assignLPath.title'),
					form: {
						user_id: {
							type: 'hidden',
							value: user_ids
						},
						cert_ids: {
							label: props.t('dashboard.user.assignLPath.form.lpathIds.label'),
							type: 'searchselect',
							options: response?.data?.data,
							required: true,
							...(!multi && { isMultiSelect: true }),
							errorMessage: props.t('dashboard.user.assignLPath.form.lpathIds.error')
						}
					},
					buttonText: props.t('dashboard.user.assignLPath.button'),
					url: `/api/user/lpath/assign`,
					method: 'POST',
				}, (data, response) => {
					// Show the results card.
					setShowAssignResults(true)
					setAssignResults(response)
					// viewContext.notification.show(props.t('dashboard.user.assignLPath.notification'), 'success', true);
				}
				);
			})
	}

	// Handle lab actions
	function assignLabs(data, callback) {

		// Check if the data is multiple.
		const multi = Array.isArray(data);
		const userIds = multi ? data.map(item => item?.row?.uuid) : [data.uuid];

		const options = {
			method: "GET",
			url: multi ? `/api/labs/all/name` : `/api/labs/all/name/?userId=${userIds[0]}`
		};
		axios
			.request(options)
			.then(function (response) {
				viewContext.modal.show({
					title: props.t('dashboard.user.assignLabs.title'),
					form: {
						userIds: {
							type: 'hidden',
							value: userIds
						},
						labNames: {
							label: props.t('dashboard.user.assignLabs.form.labNames.label'),
							type: 'searchselect',
							options: response?.data?.data,
							required: true,
							isMultiSelect: true,
							errorMessage: props.t('dashboard.user.assignLabs.form.labNames.error')
						}
					},
					buttonText: props.t('dashboard.user.assignLabs.form.button'),
					url: `/api/user/labs/assign`,
					method: 'POST',
				}, (data, response) => {

					// Show the results card.
					setShowAssignResults(true)
					setAssignResults(response)
					// viewContext.notification.show(props.t('dashboard.user.assignLabs.notification'), 'success', true);
				}
				);
			})
	}

	// Handle internship action
	function assignInternship(data, callback) {

		// Check if the data is multiple.
		const multi = Array.isArray(data);
		const user_ids = multi ? data.map(item => item?.row?.uuid) : [data.uuid];

		const options = {
			method: "GET",
			url: multi ? `/api/internship/names` : `/api/internship/names/?user_id=${user_ids[0]}`
		};
		axios
			.request(options)
			.then(function (response) {
				viewContext.modal.show({
					title: props.t('dashboard.user.assignInternship.title'),
					form: {
						user_id: {
							type: 'hidden',
							value: user_ids
						},
						internship_ids: {
							label: props.t('dashboard.user.assignInternship.form.internshipIds.label'),
							type: 'searchselect',
							options: response?.data?.data,
							required: true,
							...(!multi && { isMultiSelect: true }),
							errorMessage: props.t('dashboard.user.assignInternship.form.internshipIds.error')
						}
					},
					buttonText: props.t('dashboard.user.assignInternship.button'),
					url: `/api/user/internship/assign`,
					method: 'POST',
				}, (data, response) => {
					// Show the results card.
					setShowAssignResults(true)
					setAssignResults(response)
				}
				);
			})
	}

	function handleShowLabs(cert_id) {
		if (showScholarLabs === cert_id) {
			setShowScholarLabs('')
		} else {
			setShowScholarLabs(cert_id)
		}
	}

	// Lms actions
	function LmsCourseView(course_id) {
		viewContext.contentView.show({
			view: CourseOverView,
			course_id: course_id
		});
	}

	function addLmsDueDate(data, callback) {
		if (data.Permissions.includes('lms.due.assign')) {
			setShowLmSchedule(true)
			setScheduleUserId(data.uuid)
		} else {
			viewContext.notification.show(props.t('dashboard.lms.dueDateForm.notification'), 'warning', true);
		}
	}

	function handleCourseFilter(buId, dept_id = "") {
		axios.get(`/api/lms/dashboard/course/overview?bu_id=${buId}&dept_id=${dept_id}`)
			.then((response) => {
				setlmsEnrollmentData(response?.data?.data)
			})
			.catch((error) => {

			})
	}

	const lmsOwnerActions = {
		custom: [
			{ icon: 'calendar', action: addLmsDueDate },
		]
	}

	const disableAutocomplete = (isOrgAdmin && permission) && !stats.loading && orgScholars.length === 0;

	const disableCourseAutocomplete = (isOrgAdmin && permission) && !stats.loading && lmsEnrollmentData?.accountCoursesData?.length === 0;

	const disableMetricAutocomplete = (isOrgAdmin && permission) && !stats.loading && quizEnrollmentData?.accountMetricsData?.length === 0;

	const csvHeaders = [
		{ label: "Username", key: "username" },
		{ label: "Full Name", key: "fullname" },
		{ label: "Score", key: "Score" },
		{ label: "Email", key: "email" }
	];

	const handleCourseChange = (e, values) => {
		if (values === null) {
			setAccountCourse({
				courseId: '',
				courseTitle: ''
			})

			if (stats.data.users) {
				setOrgScholars(stats.data?.users);
				const filteredUsers = stats.data?.users?.map(({ fullname, username, lms_score }) => ({
					fullname,
					username,
					Score: lms_score
				}));
				setExportData(filteredUsers)
			}
			return;
		}
		const courseId = values?.course_id || "";
		if (!courseId) {
			setAccountCourse({
				courseId: '',
				courseTitle: ''
			})
		}
		const courseIndex = lmsEnrollmentData?.accountCoursesData?.findIndex(course => course.course_id === courseId)
		const newCourseId = lmsEnrollmentData?.accountCoursesData[courseIndex]?.course_id;
		if (accountCourse.courseId === newCourseId) {
			return;
		}
		setAccountCourse({
			courseId: newCourseId,
			courseTitle: values
		});
		const options = {
			method: "GET",
			url: `/api/lms/dashboard/course/${newCourseId}/users`,
			headers: {
				"accept": "application/json",
			},
		};
		axios
			.request(options)
			.then(function (response) {
				setOrgScholars(response.data?.data)
				const filteredUsers = response.data?.data?.map(({ fullname, username, lms_score }) => ({
					fullname,
					username,
					Score: lms_score
				}));
				setExportData(filteredUsers)
			})
			.catch((err) => {
				if (err.response.status === 401) {
					navigate('/nopermission');
				}
			});
	}

	const handleMetricChange = (e, values) => {
		if (values === null) {
			setAccountQuiz({
				metricsId: '',
				metricsName: ''
			})

			if (stats.data.users) {
				setOrgScholars(stats.data?.users);
				const filteredUsers = stats.data?.users?.map(({ fullname, username, metrics_score }) => ({
					fullname,
					username,
					Score: metrics_score
				}));
				setExportData(filteredUsers)
			}
			return;
		}
		const metricsId = values?.metrics_id || "";
		if (!metricsId) {
			setAccountQuiz({
				metricsId: '',
				metricsName: ''
			})
		}
		const metricsIndex = quizEnrollmentData?.accountMetricsData?.findIndex(metric => metric.metrics_id === metricsId)
		const newMetricsId = quizEnrollmentData?.accountMetricsData[metricsIndex]?.metrics_id;
		if (accountQuiz.metricsId === newMetricsId) {
			return;
		}
		setAccountQuiz({
			metricsId: newMetricsId,
			metricsName: values
		});
		const options = {
			method: "GET",
			url: `/api/lms/dashboard/metrics/${newMetricsId}/users`,
			headers: {
				"accept": "application/json",
			},
		};
		axios
			.request(options)
			.then(function (response) {
				setOrgScholars(response.data?.data)
				const filteredUsers = response.data?.data?.map(({ fullname, username, metrics_score }) => ({
					fullname,
					username,
					Score: metrics_score
				}));
				setExportData(filteredUsers)
			})
			.catch((err) => {
				if (err.response.status === 401) {
					navigate('/nopermission');
				}
			});
	}

	return (
		<>

			{stats?.loading ? (
				<LoadingBackdrop isOpen={true} />
			) : (
				<>
					{context?.user?.subscription === 'active' ? (
						context?.user?.onboarded ? (
							<Message
								closable
								title={props.t('dashboard.message.title')}
								text={getDashboardMessage((isOrgAdmin && permission))}
								type="info"
							/>
						) : (
							<Message
								title={'Profile Setup Pending'}
								text={'Please complete your profile setup to unlock all features and get started.'}
								type="warning"
								buttonText={'Setup Profile'}
								buttonLink={'/account'}
							/>
						)
					) : (
						<PaymentPending />
					)}
				</>
			)}
			<div style={{ backgroundColor: 'lavender', borderRadius: '8px' }}>
				<TabContext value={value} >
					{(isOrgAdmin && permission) && <Tabs
						value={value}
						onChange={handleViewChange}
						indicatorColor="primary"
						textColor="inherit"
						variant="fullWidth"
					// sx={{ display: stats?.data?.trainingModule !== 1 && 'none' }}
					>
						<Tab
							label={props.t('dashboard.user.tab')}
							value="1"
							sx={{ fontSize: '1em', fontWeight: 600, display: lmsOnly && 'none' }}
						/>
						<Tab
							label={props.t('dashboard.trainingModule.tab')}
							value="2"
							sx={{
								fontSize: '1em',
								fontWeight: 600,
								display: stats?.data?.trainingModule !== 1 && 'none'
							}}
						/>
						<Tab
							label={props.t('dashboard.lms.tab')}
							value="3"
							sx={{
								fontSize: '1em',
								fontWeight: 600,
								display: !isLMS && 'none'
							}}
						/>
						<Tab
							label="QUIZ"
							value="4"
							sx={{
								fontSize: '1em',
								fontWeight: 600,
								display: !isMetrics && 'none'
							}}
						/>
					</Tabs>}
					<div>
						<TabPanel value="1">
							<Animate type="pop">

								{showSchedule && <CertScheduleForm
									showCard={showSchedule}
									setShowCard={setShowSchedule}
									userId={scheduleUserId}
									setUserId={setScheduleUserId}
									endpoint={`/api/orgscholar/${scheduleUserId}/learning_paths/due_date`}
									dataAPI={`/api/orgscholar/${scheduleUserId}/learning_paths`}
									label={props.t('dashboard.user.dueDateForm.label')}
									message={props.t('dashboard.user.dueDateForm.message')}
								/>}
								{showAssignResults && <AssignResultsCard
									showCard={showAssignResults}
									setShowCard={setShowAssignResults}
									data={assignResults}
									translation={props.t}
								/>}
								{
									((isOrgAdmin && permission) && !stats.loading) &&
									<div
										id='accountUsersContainer'
										className={Styles.accountUsersContainer}
										style={isSticky ? {
											borderBottom: "0.2em solid lavender",
											borderTop: "0.2em solid lavender",
											borderRadius: 0
										} : {}}
									>
										{props.t('dashboard.user.info')}
										<Autocomplete
											size='small'
											value={formData.userName || null}
											id="combo-box-demo"
											options={orgScholars}
											onChange={handleChange}
											getOptionLabel={(option) => (option ? option?.fullname : '')}
											getOptionSelected={(option, value) => option.fullname === value.userName}
											disabled={disableAutocomplete}
											sx={{ width: 300 }}
											renderInput={(params) =>
												<TextField
													{...params}
													label={props.t('dashboard.user.select.label')}
													error={disableAutocomplete}
													helperText={disableAutocomplete ? props.t('dashboard.user.select.helperText') : ""}
												/>
											}

										/>
									</div>
								}

								{/* Show Cards */}
								<Grid cols={"3"} centered={true}>
									<div id='clickable' className={(isOrgAdmin && permission) ? '' : Styles.clickable} onClick={() => { handleNavigate('/lpaths') }}>
										<Stat
											loading={stats?.loading} value={progressDict?.cert} label={props.t('dashboard.user.stat.LPaths')}
											icon="award" iconColor="#289055"
										/>
									</div>
									<div id='clickable' className={(isOrgAdmin && permission) ? '' : Styles.clickable} onClick={() => { handleNavigate('/labs') }}>
										<Stat
											loading={stats?.loading} value={progressDict?.lab} label={props.t('dashboard.user.stat.labs')}
											icon="monitor" iconColor="#FF1616"
										/>
									</div>
									<div id='clickable' className={(isOrgAdmin && permission) ? '' : Styles.clickable} onClick={() => { handleNavigate('/projects') }}>
										<Stat
											loading={stats?.loading} value={progressDict?.project} label={props.t('dashboard.user.stat.capstoneProject')}
											icon="folder" iconColor="#ffb6c1"
										/>
									</div>
									<div id='clickable' className={(isOrgAdmin && permission) ? '' : Styles.clickable} onClick={() => { handleNavigate('/streams') }}>
										<Stat
											loading={stats?.loading} value={progressDict?.stream} label={props.t('dashboard.user.stat.streams')}
											icon="code" iconColor="#1450a0"
										/>
									</div>
									<div id='clickable' className={(isOrgAdmin && permission) ? '' : Styles.clickable} onClick={() => { handleNavigate('/quiz') }}>
										<Stat
											loading={stats?.loading} value={progressDict?.quiz} label={props.t('dashboard.user.stat.quiz')}
											icon="help-circle" iconColor="#FFBF00"
										/>
									</div>
									{/* {context?.user?.plan?.includes('lms') && <div id='clickable' className={Styles.clickable} onClick={() => { navigate('/lms/courses/all') }}>
										<Stat
											loading={stats?.loading} value={progressDict?.courses} label={props.t('dashboard.user.stat.courses')}
											icon="book-open" iconColor="#800080"
										/>
									</div>} */}
								</Grid>
								{/* charts and progress */}
								<Grid cols="2">
									<Card title={props.t('dashboard.user.completed')} loading={stats?.loading}>
										{stats?.loading && progressDict?.progress?.length === 0 ?
											(
												<>
													<Loader />
												</>
											) : (
												<div id='progressBarsContainer'
													style={{
														display: 'flex',
														flexDirection: 'column',
														justifyContent: 'center'
													}}
												>
													{progressDict?.progress?.map(item => (
														<ProgressBar
															key={item.name}
															label={item.name}
															progress={`${item.value}%`}
														/>
													))}
												</div>
											)}
									</Card>
									<Card title={props.t('dashboard.user.chart.badge')} restrictHeight>
										{stats.loading && progressDict.progress?.length === 0 ?
											(
												<>
													<Loader />
												</>
											) : (
												<Chart type="donut"
													legend={true}
													data={progressDict?.badges}
													loading={stats.loading}
													color={"gold"}
												/>
											)}
									</Card>
								</Grid>

								{/* Add user details example their score and profile details etc */}
								{
									(isOrgAdmin && permission) && orgScholars.length >= 1 &&
									<>
										<Card title={props.t('dashboard.user.scholars.title')} >
											<div id='exportCSVBtnContainer' className={Styles.exportCSVContainer}>
												<CSVLink
													data={exportData}
													headers={csvHeaders}
													filename={'scholars.csv'}
													className={Styles.exportBtn}
													target="_blank"
												>
													{props.t('dashboard.user.scholars.button.exportCsv')}
												</CSVLink>
											</div>
											<span id='legendContainer' className={Styles.legendContainer}>
												<div className={Styles.itemsList}><Icon image='calendar' /><p>{': '}{props.t('dashboard.user.scholars.item.dueDate')}</p></div>
												<div className={Styles.itemsList}><Icon image='file' /><p>{': '}{props.t('dashboard.user.scholars.item.assignLPath')}</p></div>
												<div className={Styles.itemsList}><Icon image='monitor' /><p>{': '}{props.t('dashboard.user.scholars.item.assignLab')}</p></div>
												<div className={Styles.itemsList}><Icon image='package' /><p>{': '}{props.t('dashboard.user.scholars.item.assignInternship')}</p></div>
											</span>
											<Table
												search
												selectable
												data={orgScholars.map((scholar, index) => ({
													id: index + 1,
													Name: scholar.fullname,
													Email: scholar.email,
													Score: scholar.lab_score + scholar.stream_score,
													Active: scholar.Active,
													Role: scholar.role,
													Permissions: scholar.permissions,
													uuid: scholar.id
												}))}
												hide={['uuid', 'Permissions']}
												badge={{ col: 'Score', color: 'blue' }}
												actions={ownerActions}
												bulkActions={{
													custom: [{ text: props.t('dashboard.user.scholars.table.button.assignLpath'), color: "green", action: assignCertificates },
													{ text: props.t('dashboard.user.scholars.table.button.assignLab'), color: "green", action: assignLabs },
													{ text: props.t('dashboard.user.scholars.table.button.assignInternship'), color: "green", action: assignInternship }]
												}}
												translation='dashboard.table'
											/>
										</Card>
										<Card title={props.t('dashboard.user.progress.title')}>
											{show ?
												<>
													<Chart
														type='bar'
														loading={certData?.loading}
														data={certData?.data?.[0]}
														legend={true}
													/>
												</> :
												<div id='scholarProgress' className={Styles.scholarProgress}>
													<List
														sx={{ width: '100%', bgcolor: 'background.paper' }}
														component="nav"
														aria-labelledby="nested-list-subheader"
														subheader={
															<div>
																<span style={{ display: 'flex', gap: '10px' }}>
																	<p><StopIcon sx={{ color: '#f0fff0' }} /> {props.t('dashboard.user.progress.subHeader.completed')}</p>
																	<p><StopIcon sx={{ color: '#fff4f4' }} /> {props.t('dashboard.user.progress.subHeader.notCompleted')}</p>
																</span>
																<p style={{ fontSize: '20px', marginBottom: '20px', marginTop: '20px', color: '#000' }}>{props.t('dashboard.user.progress.subHeader.scholarLPath')}</p>

															</div>
														}
													>

														{
															scholarCertData?.map((data, index) => {
																return (
																	<div key={`${data?.cert_name}_${data?.cert_id}_${index}`}>
																		<ListItemButton
																			sx={{
																				borderTop: '0.5px solid #d3d3d3',
																				borderBottom: '1px solid #d3d3d3',
																				color: '#000',

																			}}
																			onClick={() => { handleShowLabs(data?.cert_id) }}
																		>
																			<ListItemText primary={data?.cert_name} secondary={props.t('dashboard.user.progress.listItem.completed.header', { completionPercentage: data?.completion_percentage })} />

																			<div style={{ display: 'flex', width: '80%' }}>
																				<ListItemText primary={`Due Date`} secondary={data?.due_date} />
																				<ListItemText primary={`Start Date`} secondary={data?.start_date ? data?.start_date : props.t('dashboard.user.progress.listItem.completed.begin')} />
																				<ListItemText primary={`Finish Date`} secondary={data?.finish_date ? data?.finish_date : props.t('dashboard.user.progress.listItem.completed.finish')} />
																				<ListItemText primary={`Score`} secondary={data?.score} />
																			</div>
																			{showScholarLabs === data?.cert_id ? <ExpandLess /> : <ExpandMore />}
																		</ListItemButton>
																		<Collapse in={showScholarLabs === data?.cert_id} timeout="auto" unmountOnExit>
																			<List
																				subheader={
																					<ListSubheader >
																						{props.t('dashboard.user.progress.list.header', { lpathName: data?.cert_name })}
																					</ListSubheader>
																				}
																			>
																				{
																					data?.certLabs?.map((lab, labIndex) => {
																						return (
																							<div key={`${data?.cert_id}_${lab?.lab_name}_${labIndex}`} >
																								<ListItem
																									sx={{
																										pl: 4,
																										// backgroundColor: lab?.lab_completion === 100 && "#f0fff0"
																										color: '#000'
																									}}
																								>
																									<ListItemText primary={lab?.lab_name} secondary={props.t('dashboard.user.progress.list.listItem.progress', { labCompletion: lab?.lab_completion })} />
																									<div className={Styles.labCompletionDate}>
																										<ListItemText primary={`Start Date`} secondary={lab?.lab_start_date ? lab?.lab_start_date : props.t('dashboard.user.progress.list.listItem.begin')} />
																										<ListItemText primary={`Finish Date`} secondary={lab?.lab_finish_date ? lab?.lab_finish_date : props.t('dashboard.user.progress.list.listItem.inProgress')} />
																										<ListItemText primary={`Score`} secondary={lab?.lab_score} />
																									</div>
																								</ListItem>

																							</div>
																						)
																					})
																				}
																			</List>
																		</Collapse>
																	</div>
																)
															})
														}
													</List>
												</div>}
										</Card>
									</>
								}


							</Animate >
						</TabPanel>
						{/* Training Modules Content */}
						<TabPanel value="2">
							<Animate type="pop">
								{
									(isOrgAdmin && permission) && !stats.loading && orgScholars.length >= 1 &&
									<div
										id='accountUsersContainer'
										className={`${Styles.accountUsersContainer} `}

									>
										{props.t('dashboard.trainingModule.info')}
										<Autocomplete
											size='small'
											value={formData.userName || null}
											id="combo-box-demo"
											options={orgScholars}
											onChange={handleChange}
											getOptionLabel={(option) => (option ? option?.fullname : '')}
											getOptionSelected={(option, value) => option.fullname === value.userName}
											sx={{ width: 300 }}
											renderInput={(params) => <TextField {...params} label={props.t('dashboard.trainingModule.select.label')} />}

										/>
									</div>
								}

								{
									show ?
										<Grid cols="2">
											<Card title={props.t('dashboard.trainingModule.card.completion')} loading={stats.loading}>
												<Chart
													type='bar'
													loading={trainingStat.loading}
													data={trainingStat?.data?.completionRate[0]}
													legend={true}
												/>
											</Card>
											<Card title={props.t('dashboard.trainingModule.card.completed')} restrictHeight>
												<Chart
													type='pie'
													loading={trainingStat.loading}
													data={trainingStat?.data?.completion[0]}
													legend={true}
													color="gold"
												/>
											</Card>
										</Grid> :
										<>
											<Card title={props.t('dashboard.trainingModule.card.overView')}>
												<Chart
													type='bar'
													loading={stats?.loading}
													data={userTrainingStats?.ProgressOverviewChart}
													legend={true}
												/>
											</Card>
											<Card title={props.t('dashboard.trainingModule.card.module')} last>


												<div id='scholarProgress' className={Styles.scholarProgress}>
													<List
														sx={{ width: '100%', bgcolor: 'background.paper' }}
														component="nav"
														aria-labelledby="nested-list-subheader"
													>

														{
															userTrainingStats?.userTrainingModules?.map((data, index) => {
																return (
																	<div key={`${data?.lab_name}_${data?.lab_id}_${index}`}>
																		<ListItemButton
																			sx={{
																				borderTop: '0.5px solid #d3d3d3',
																				borderBottom: index === userTrainingStats?.userTrainingModules?.length - 1 && '0.5px solid #d3d3d3',
																				color: '#000',

																			}}
																			onClick={() => { handleShowLabs(data?.lab_id) }}
																		>
																			<ListItemText sx={{ width: '20%' }} primary={data?.lab_name} secondary={props.t('dashboard.trainingModule.progress.listItem.completion', { completionPercentage: data?.lab_completion_percentage })} />

																			<div style={{ display: 'flex', width: '70%' }}>
																				<ListItemText primary={props.t('dashboard.trainingModule.progress.listItem.start.primary')} secondary={data?.start_date ? data?.start_date : props.t('dashboard.trainingModule.progress.listItem.start.secondary')} />
																				<ListItemText primary={props.t('dashboard.trainingModule.progress.listItem.status')} secondary={data?.lab_status} />
																				{<ListItemText primary={props.t('dashboard.trainingModule.progress.listItem.score')} secondary={data?.lab_score} />}


																			</div>
																			{showScholarLabs === data?.lab_id ? <ExpandLess /> : <ExpandMore />}
																		</ListItemButton>
																		<Collapse in={showScholarLabs === data?.lab_id} timeout="auto" unmountOnExit>
																			<List
																				subheader={
																					<ListSubheader >
																						{props.t('dashboard.trainingModule.progress.list.header', { labName: data?.lab_name })}
																					</ListSubheader>
																				}
																			>
																				{
																					data?.trainingNodes?.map((node, nodeIndex) => {
																						return (
																							<div key={`${data?.lab_id}_${node?.node_name}_${nodeIndex}`} >
																								<ListItem
																									sx={{
																										pl: 4,
																										// backgroundColor: node?.node_completion === 100 && "#f0fff0"
																										color: '#000',

																									}}
																								>

																									<ListItemText sx={{ width: '20%' }} primary={node?.node_name} />
																									<div className={Styles.labCompletionDate} style={{ width: '70%', display: 'flex', justifyContent: 'space-between' }}>
																										<ListItemText primary={props.t('dashboard.trainingModule.progress.list.listItem.status')} secondary={node?.node_status} sx={{ width: '10%' }} />
																										<ListItemText primary={props.t('dashboard.trainingModule.progress.list.listItem.attempts')} secondary={node?.node_attempt_cleared} />
																									</div>
																								</ListItem>

																							</div>
																						)
																					})
																				}
																			</List>
																		</Collapse>

																	</div>
																)
															})
														}
													</List>
												</div>

											</Card>
										</>
								}
							</Animate >
						</TabPanel>
						<TabPanel value="3">
							<Animate type="pop">
								{showLmsSchedule && <CertScheduleForm
									showCard={showLmsSchedule}
									setShowCard={setShowLmSchedule}
									userId={scheduleUserId}
									setUserId={setScheduleUserId}
									endpoint={`/api/orgscholar/${scheduleUserId}/courses/due_date`}
									dataAPI={`/api/orgscholar/${scheduleUserId}/courses`}
									label={props.t('dashboard.lms.dueDateForm.label')}
									message={props.t('dashboard.lms.dueDateForm.message')}
								/>}
								{/* Training Hub content */}
								{(isOrgAdmin && permission) && isLMS &&
									<>
										<div
											id='accountUsersContainer'
											className={Styles.accountUsersContainer}
										>
											{props.t('dashboard.lms.info')}
											<Autocomplete
												size='small'
												value={formData.userName || null}
												id="combo-box-demo"
												options={orgScholars}
												onChange={handleChange}
												getOptionLabel={(option) => (option ? option?.fullname : '')}
												getOptionSelected={(option, value) => option.fullname === value.userName}
												sx={{ width: 300 }}
												disabled={disableAutocomplete}
												renderInput={(params) =>
													<TextField
														{...params}
														label={props.t('dashboard.lms.select.label')}
														error={disableAutocomplete}
														helperText={disableAutocomplete ? props.t('dashboard.lms.select.helperText') : "Applying course filter shows only course users."}
													/>
												}
											/>
										</div>
										<Card title={props.t('dashboard.lms.trainingHub.title')}>
											<div className={Styles.lmsContainer}>
												<div>
													<Typography variant='h6' sx={{ fontWeight: 600, mb: '0.9em' }}>
														{props.t('dashboard.lms.chartHeader.progress')}
													</Typography>
													<Chart
														type='pie'
														loading={lmsOverview?.loading}
														data={lmsOverview?.data?.courseTotalCompletion}
														legend={true}
														color='gold'
													/>
												</div>
												<Divider orientation="vertical" flexItem />
												<div>
													<Typography variant='h6' sx={{ fontWeight: 600, mb: '0.9em' }}>
														{props.t('dashboard.lms.chartHeader.completion')}
													</Typography>
													<Chart
														type='bar'
														loading={lmsOverview?.loading}
														data={lmsOverview?.data?.courseCompletionRate}
														legend={true}
														color='blue'
													/>
												</div>
											</div>
										</Card>
										<Card title={show ? props.t('dashboard.lms.courseView.title.show') :
											props.t('dashboard.lms.courseView.title.default', { fullName: formData?.userName?.fullname })}>
											{show ? <div className={Styles.courseOverviewContainer}>
												<span>
													<p>{props.t('dashboard.lms.courseView.filter')}</p>
													<select
														onChange={(e) => { setBuID(e.target.value); handleCourseFilter(e.target.value) }}
													>
														<option value=''>{props.t('dashboard.lms.courseView.select.BU')}</option>
														{lmsEnrollmentData.bu_ids?.map((bu, index) => (
															<option key={`${bu}_${index}`} value={bu?.bu_id}>{bu?.bu_name}</option>
														))}
													</select>
													{lmsEnrollmentData?.dept_ids && <select
														onChange={(e) => { handleCourseFilter(buId, e.target.value) }}
													>
														<option value="">{props.t('dashboard.lms.courseView.select.dept')}</option>
														{lmsEnrollmentData?.dept_ids?.map((dept, index) => (
															<option key={`${dept}_${index}`} value={dept?.dept_id}>{dept?.dept_name}</option>
														))}
													</select>}
												</span>
												<List
													sx={{ width: '100%', bgcolor: 'background.paper' }}
												>
													{
														lmsEnrollmentData?.courseEnrollmentData?.map((course, index) => {
															return (
																<div key={`${course.course_title}_${index}`}>
																	{index === 0 && <Divider orientation='horizontal' />}
																	<ListItemButton
																		onClick={() => { LmsCourseView(course.course_id) }}
																		sx={{ height: '4em' }}
																	>
																		<ListItemText sx={{ width: '10%' }} primary={course.course_title} />
																		<ListItemText primary={props.t('dashboard.lms.courseView.list.item.status')} secondary={course.course_status} />
																		<ListItemText primary={props.t('dashboard.lms.courseView.list.item.enrolled')} secondary={course.users_enrolled} />
																		<ListItemText primary={props.t('dashboard.lms.courseView.list.item.completed')} secondary={course.users_completed} />
																	</ListItemButton>
																	<Divider orientation='horizontal' />
																</div>
															)
														})
													}
												</List>
											</div> :
												<List
													sx={{ width: '100%', bgcolor: 'background.paper' }}
												>
													{
														userCourses?.map((course, index) => {
															return (
																<div>
																	{index === 0 && <Divider orientation='horizontal' />}
																	<ListItem
																		sx={{ height: '4em' }}
																		key={`${course.course_title}_${index}`}
																	>
																		<ListItemText sx={{ width: '10%' }} primary={course.course_title} secondary={props.t('dashboard.lms.courseView.list.userCourseItem.completion', { completionPercentage: course.completion_percentage })} />
																		<ListItemText sx={{ width: '10%' }} primary={props.t('dashboard.lms.courseView.list.userCourseItem.dueDate.primary')} secondary={course.due_date ? new Date(course.due_date).toString().slice(4, 16) : props.t('dashboard.lms.courseView.list.userCourseItem.dueDate.secondary')} />
																		<ListItemText primary={props.t('dashboard.lms.courseView.list.userCourseItem.completionStatus.primary')} secondary={course.completion_status ? course.completion_status : props.t('dashboard.lms.courseView.list.userCourseItem.completionStatus.primary')} />
																		<ListItemText primary={props.t('dashboard.lms.courseView.list.userCourseItem.score.primary')} secondary={course.score} />
																	</ListItem>
																	<Divider orientation='horizontal' />
																</div>
															)
														})
													}
												</List>
											}
										</Card>
										{
											((isOrgAdmin && permission) && !stats.loading && isLMS) &&
											<div
												id='accountUsersContainer'
												className={Styles.accountUsersContainer}
											>
												{props.t('dashboard.lms.course.info')}
												<Autocomplete
													size='small'
													value={accountCourse.courseTitle || null}
													id="combo-box-demo"
													options={lmsEnrollmentData?.accountCoursesData || []}
													onChange={handleCourseChange}
													getOptionLabel={(option) => (option ? option?.course_title : '')}
													getOptionSelected={(option, value) => option.course_title === value.courseTitle}
													disabled={disableCourseAutocomplete}
													sx={{ width: 300 }}
													renderInput={(params) =>
														<TextField
															{...params}
															label={props.t('dashboard.lms.course.select.label')}
															error={disableCourseAutocomplete}
															helperText={disableCourseAutocomplete ? props.t('dashboard.lms.course.select.helperText') : ""}
														/>
													}

												/>
											</div>
										}
										<Card title={props.t('dashboard.lms.scholars.title')} >
											<div id='exportCSVBtnContainer' className={Styles.exportCSVContainer}>
												<CSVLink
													data={exportData}
													headers={csvHeaders}
													filename={'scholars.csv'}
													className={Styles.exportBtn}
													target="_blank"
												>
													{props.t('dashboard.user.scholars.button.exportCsv')}
												</CSVLink>
											</div>
											<span id='legendContainer' className={Styles.legendContainer}>
												<div className={Styles.itemsList}><Icon image='calendar' /><p>{': '}{props.t('dashboard.lms.scholars.legend.courseDueDate')}</p></div>
											</span>
											<Table
												search
												data={orgScholars.map((scholar, index) => ({
													id: index + 1,
													Name: scholar.fullname,
													Email:scholar.email,
													Score: scholar.lms_score,
													Active: new Date(scholar.Active).toString().slice(4, 16),
													Role: scholar.role,
													uuid: scholar.id
												}))}
												hide={['uuid']}
												badge={{ col: 'Score', color: 'blue' }}
												actions={lmsOwnerActions}
												translation='dashboard.table'
											/>
										</Card>

									</>
								}
							</Animate>
						</TabPanel>
						<TabPanel value="4">
							<Animate type="pop">
								{/* DIY QUIZ content */}
								{(isOrgAdmin && permission) && isMetrics &&
									<>
										<div
											id='accountUsersContainer'
											className={Styles.accountUsersContainer}
										>
											{props.t('dashboard.lms.info')}
											<Autocomplete
												size='small'
												value={formData.userName || null}
												id="combo-box-demo"
												options={orgScholars}
												onChange={handleChange}
												getOptionLabel={(option) => (option ? option?.fullname : '')}
												getOptionSelected={(option, value) => option.fullname === value.userName}
												sx={{ width: 300 }}
												disabled={disableAutocomplete}
												renderInput={(params) =>
													<TextField
														{...params}
														label={props.t('dashboard.lms.select.label')}
														error={disableAutocomplete}
														helperText={disableAutocomplete ? props.t('dashboard.lms.select.helperText') : ""}
													/>
												}
											/>
										</div>
										<Card title={props.t('dashboard.metrics.card.title')}>
											<div className={Styles.lmsContainer}>
												<div>
													<Typography variant='h6' sx={{ fontWeight: 600, mb: '0.9em' }}>
														{props.t('dashboard.lms.chartHeader.progress')}
													</Typography>
													<Chart
														type='pie'
														loading={quizOverview?.loading}
														data={quizOverview?.data?.courseTotalCompletion}
														legend={true}
														color='gold'
													/>
												</div>
												<Divider orientation="vertical" flexItem />
												<div>
													<Typography variant='h6' sx={{ fontWeight: 600, mb: '0.9em' }}>
														{props.t('dashboard.metrics.chartHeader.completion')}
													</Typography>
													<Chart
														type='bar'
														loading={quizOverview?.loading}
														data={quizOverview?.data?.courseCompletionRate}
														legend={true}
														color='blue'
													/>
												</div>
											</div>
										</Card>
										<Card
											title={show ? props.t('dashboard.metrics.quizView.title.show') : props.t('dashboard.metrics.quizView.title.default', { fullName: formData?.userName?.fullname })}
										>
											{show ? <div className={Styles.courseOverviewContainer}>
												<List
													sx={{ width: '100%', bgcolor: 'background.paper' }}
												>
													{
														quizEnrollmentData?.metricsEnrollmentData?.map((metric, index) => {
															return (
																<div key={`${metric.metric_name}_${index}`}>
																	{index === 0 && <Divider orientation='horizontal' />}
																	<ListItemButton
																		onClick={() => { }}
																		sx={{ height: '4em' }}
																	>
																		<ListItemText sx={{ width: '10%' }} primary={metric.metric_name} />
																		<ListItemText primary={props.t('dashboard.metrics.quizView.list.item.enrolled')} secondary={metric?.users_enrolled} />
																		<ListItemText primary={props.t('dashboard.metrics.quizView.list.item.completed')} secondary={metric?.users_completed} />
																	</ListItemButton>
																	<Divider orientation='horizontal' />
																</div>
															)
														})
													}
												</List>
											</div> :
												<List
													sx={{ width: '100%', bgcolor: 'background.paper' }}
												>
													{
														userMetrics?.map((metric, index) => {
															return (
																<div>
																	{index === 0 && <Divider orientation='horizontal' />}
																	<ListItem
																		sx={{ height: '4em' }}
																		key={`${metric.name}_${index}`}
																	>
																		<ListItemText sx={{ width: '10%' }} primary={metric.name}
																			secondary={props.t('dashboard.metrics.quizView.list.userMetricItem.completion', { completionPercentage: metric.completion_percentage })}
																		/>
																		<ListItemText sx={{ width: '10%' }} primary={props.t('dashboard.metrics.quizView.list.userMetricItem.score')} secondary={metric.score} />
																		<ListItemText primary={props.t('dashboard.metrics.quizView.list.userMetricItem.completionStatus.primary')} secondary={metric.status ? metric.status : props.t('dashboard.metrics.quizView.list.userMetricItem.completionStatus.secondary')} />
																	</ListItem>
																	<Divider orientation='horizontal' />
																</div>
															)
														})
													}
												</List>
											}
										</Card>
										{
											((isOrgAdmin && permission) && !stats.loading && isMetrics) &&
											<div
												id='accountUsersContainer'
												className={Styles.accountUsersContainer}
											>
												{props.t('dashboard.metrics.quiz.info')}
												<Autocomplete
													size='small'
													value={accountQuiz.metricsName || null}
													id="combo-box-demo"
													options={quizEnrollmentData?.accountMetricsData || []}
													onChange={handleMetricChange}
													getOptionLabel={(option) => (option ? option?.metrics_name : '')}
													getOptionSelected={(option, value) => option.metrics_name === value.metricsName}
													disabled={disableMetricAutocomplete}
													sx={{ width: 300 }}
													renderInput={(params) =>
														<TextField
															{...params}
															label={props.t('dashboard.metrics.quiz.select.label')}
															error={disableMetricAutocomplete}
															helperText={disableMetricAutocomplete ? props.t('dashboard.metrics.quiz.select.helperText') : ""}
														/>
													}

												/>
											</div>
										}
										<Card title={props.t('dashboard.lms.scholars.title')} >
											<div id='exportCSVBtnContainer' className={Styles.exportCSVContainer}>
												<CSVLink
													data={exportData}
													headers={csvHeaders}
													filename={'scholars.csv'}
													className={Styles.exportBtn}
													target="_blank"
												>
													{props.t('dashboard.user.scholars.button.exportCsv')}
												</CSVLink>
											</div>
											<Table
												search
												data={orgScholars.map((scholar, index) => ({
													id: index + 1,
													Name: scholar.fullname,
													Email: scholar.email,
													Score: scholar.metrics_score,
													Active: new Date(scholar.Active).toString().slice(4, 16),
													Role: scholar.role,
													uuid: scholar.id
												}))}
												hide={['uuid']}
												badge={{ col: 'Score', color: 'blue' }}
												// actions={lmsOwnerActions}
												translation='dashboard.table'
											/>
										</Card>

									</>
								}
							</Animate>
						</TabPanel>
					</div>
				</TabContext>
			</div>
		</>

	);
}

